import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { DOMAIN_TYPES, getLandingPageConfig, parseSubdomain } from '../utils/domainResolver';

const DomainContext = createContext(null);

export function DomainProvider({ children }) {
  const [hostname, setHostname] = useState(null);
  const [landingPageConfig, setLandingPageConfig] = useState(null);
  const [type, setType] = useState(DOMAIN_TYPES.UNKNOWN);
  const [branding, setBranding] = useState({});
  const [isCustomDomain, setIsCustomDomain] = useState(false);

  useEffect(() => {
    const host = window.location.hostname.split(':')[0].toLowerCase();
    setHostname(host);
    const config = getLandingPageConfig(host);
    if (config) {
      setLandingPageConfig(config);
      setType(config.type);
      setBranding(config.branding);
      setIsCustomDomain(true);
    } else {
      const parsed = parseSubdomain(host);
      setType(parsed.type);
      setLandingPageConfig(null);
      setIsCustomDomain(false);
    }
  }, []);

  const resolve = useCallback(async (host) => {
    const cleanHost = host.split(':')[0].toLowerCase();
    const config = getLandingPageConfig(cleanHost);
    if (config) {
      setLandingPageConfig(config);
      setType(config.type);
      setBranding(config.branding);
      setIsCustomDomain(true);
    } else {
      const parsed = parseSubdomain(cleanHost);
      setType(parsed.type);
      setLandingPageConfig(null);
      setIsCustomDomain(false);
    }
  }, []);

  const clearDomain = useCallback(() => {
    setHostname(null);
    setLandingPageConfig(null);
    setType(DOMAIN_TYPES.UNKNOWN);
    setBranding({});
    setIsCustomDomain(false);
  }, []);

  const getTargetRoute = useCallback(() => {
    switch (type) {
      case DOMAIN_TYPES.PAAS: return '/paas-admin/login';
      case DOMAIN_TYPES.SAAS: return '/saas-admin/login';
      case DOMAIN_TYPES.CLIENT: return '/client-admin/login';
      case DOMAIN_TYPES.APP: return '/login';
      default: return '/';
    }
  }, [type]);

  const value = {
    hostname,
    type,
    landingPageConfig,
    isLandingPage: landingPageConfig !== null,
    paasId: null,
    saasId: null,
    tenantId: null,
    branding,
    isResolved: !!hostname,
    isLoading: false,
    error: null,
    isCustomDomain,
    customDomain: hostname,
    resolve,
    clearDomain,
    getTargetRoute,
    setDomainData: () => {}
  };

  return (
    <DomainContext.Provider value={value}>
      {children}
    </DomainContext.Provider>
  );
}

export function useDomain() {
  const context = useContext(DomainContext);
  if (!context) {
    throw new Error('useDomain must be used within a DomainProvider');
  }
  return context;
}

export default DomainContext;